import {
  FacilityFlowCoreModulesPlanningEntitiesTaskScheduleRuleType,
  FacilityFlowCoreModulesPlanningEntitiesWeekdayOrdinal,
} from '../api/generated/models';
import { SystemDayOfWeek } from '../api/generated/models';
import { i18n } from '../i18n';

/**
 * Readable mapping for TaskScheduleRuleType enum values.
 * Backend enum: 1=Weekly, 2=BiWeekly, 3=Monthly, 4=Seasonal
 */
export const ScheduleRuleType = {
  Weekly: FacilityFlowCoreModulesPlanningEntitiesTaskScheduleRuleType.NUMBER_1,
  BiWeekly: FacilityFlowCoreModulesPlanningEntitiesTaskScheduleRuleType.NUMBER_2,
  Monthly: FacilityFlowCoreModulesPlanningEntitiesTaskScheduleRuleType.NUMBER_3,
  Seasonal: FacilityFlowCoreModulesPlanningEntitiesTaskScheduleRuleType.NUMBER_4,
} as const;

export const ScheduleRuleTypeLabels: Record<number, string> = {
  get [ScheduleRuleType.Weekly]() { return i18n.t('common:scheduleRule.types.weekly'); },
  get [ScheduleRuleType.BiWeekly]() { return i18n.t('common:scheduleRule.types.biWeekly'); },
  get [ScheduleRuleType.Monthly]() { return i18n.t('common:scheduleRule.types.monthly'); },
  get [ScheduleRuleType.Seasonal]() { return i18n.t('common:scheduleRule.types.seasonal'); },
};

export const ScheduleRuleTypeDescriptions: Record<number, string> = {
  get [ScheduleRuleType.Weekly]() { return i18n.t('common:scheduleRule.descriptions.weekly'); },
  get [ScheduleRuleType.BiWeekly]() { return i18n.t('common:scheduleRule.descriptions.biWeekly'); },
  get [ScheduleRuleType.Monthly]() { return i18n.t('common:scheduleRule.descriptions.monthly'); },
  get [ScheduleRuleType.Seasonal]() { return i18n.t('common:scheduleRule.descriptions.seasonal'); },
};

/**
 * Readable mapping for WeekdayOrdinal (position of a weekday within a month).
 * Backend enum: 1=First, 2=Second, 3=Third, 4=Fourth, 5=Last
 */
export const WeekdayOrdinal = {
  First: FacilityFlowCoreModulesPlanningEntitiesWeekdayOrdinal.NUMBER_1,
  Second: FacilityFlowCoreModulesPlanningEntitiesWeekdayOrdinal.NUMBER_2,
  Third: FacilityFlowCoreModulesPlanningEntitiesWeekdayOrdinal.NUMBER_3,
  Fourth: FacilityFlowCoreModulesPlanningEntitiesWeekdayOrdinal.NUMBER_4,
  Last: FacilityFlowCoreModulesPlanningEntitiesWeekdayOrdinal.NUMBER_5,
} as const;

export const OrdinalLabels: Record<number, string> = {
  get [WeekdayOrdinal.First]() { return i18n.t('common:scheduleRule.ordinals.first'); },
  get [WeekdayOrdinal.Second]() { return i18n.t('common:scheduleRule.ordinals.second'); },
  get [WeekdayOrdinal.Third]() { return i18n.t('common:scheduleRule.ordinals.third'); },
  get [WeekdayOrdinal.Fourth]() { return i18n.t('common:scheduleRule.ordinals.fourth'); },
  get [WeekdayOrdinal.Last]() { return i18n.t('common:scheduleRule.ordinals.last'); },
};

export const OrdinalOptions = [
  WeekdayOrdinal.First,
  WeekdayOrdinal.Second,
  WeekdayOrdinal.Third,
  WeekdayOrdinal.Fourth,
  WeekdayOrdinal.Last,
];

/**
 * Readable mapping for System.DayOfWeek.
 * .NET DayOfWeek: 0=Sunday, 1=Monday, ..., 6=Saturday
 */
export const Weekday = {
  Sunday: SystemDayOfWeek.NUMBER_0,
  Monday: SystemDayOfWeek.NUMBER_1,
  Tuesday: SystemDayOfWeek.NUMBER_2,
  Wednesday: SystemDayOfWeek.NUMBER_3,
  Thursday: SystemDayOfWeek.NUMBER_4,
  Friday: SystemDayOfWeek.NUMBER_5,
  Saturday: SystemDayOfWeek.NUMBER_6,
} as const;

/** Weekday order for pickers (Monday-first). */
export const WeekdayOptions = [
  Weekday.Monday, Weekday.Tuesday, Weekday.Wednesday,
  Weekday.Thursday, Weekday.Friday, Weekday.Saturday, Weekday.Sunday,
];

export const WeekdayLabels: Record<number, string> = {
  get [Weekday.Monday]() { return i18n.t('common:scheduleRule.weekdays.monday'); },
  get [Weekday.Tuesday]() { return i18n.t('common:scheduleRule.weekdays.tuesday'); },
  get [Weekday.Wednesday]() { return i18n.t('common:scheduleRule.weekdays.wednesday'); },
  get [Weekday.Thursday]() { return i18n.t('common:scheduleRule.weekdays.thursday'); },
  get [Weekday.Friday]() { return i18n.t('common:scheduleRule.weekdays.friday'); },
  get [Weekday.Saturday]() { return i18n.t('common:scheduleRule.weekdays.saturday'); },
  get [Weekday.Sunday]() { return i18n.t('common:scheduleRule.weekdays.sunday'); },
};

export const MonthLabels: Record<number, string> = {
  get 1() { return i18n.t('common:scheduleRule.months.1'); },
  get 2() { return i18n.t('common:scheduleRule.months.2'); },
  get 3() { return i18n.t('common:scheduleRule.months.3'); },
  get 4() { return i18n.t('common:scheduleRule.months.4'); },
  get 5() { return i18n.t('common:scheduleRule.months.5'); },
  get 6() { return i18n.t('common:scheduleRule.months.6'); },
  get 7() { return i18n.t('common:scheduleRule.months.7'); },
  get 8() { return i18n.t('common:scheduleRule.months.8'); },
  get 9() { return i18n.t('common:scheduleRule.months.9'); },
  get 10() { return i18n.t('common:scheduleRule.months.10'); },
  get 11() { return i18n.t('common:scheduleRule.months.11'); },
  get 12() { return i18n.t('common:scheduleRule.months.12'); },
};

/** Number of days in a month (leap year, so February allows 29). */
export function daysInMonth(month: number): number {
  return new Date(2024, month, 0).getDate();
}

/** Minimal shape needed to render a rule; matches both the DTO and pending/create requests. */
export interface FormattableRule {
  type?: number;
  weekdays?: number[] | null;
  weekday?: number;
  ordinal?: number;
  windowFromMonth?: number | null;
  windowFromDay?: number | null;
  windowToMonth?: number | null;
  windowToDay?: number | null;
}

function formatWindow(rule: FormattableRule): string | null {
  if (!rule.windowFromMonth || !rule.windowFromDay || !rule.windowToMonth || !rule.windowToDay) {
    return null;
  }
  return i18n.t('common:scheduleRule.formats.window', {
    fromDay: rule.windowFromDay,
    fromMonth: MonthLabels[rule.windowFromMonth],
    toDay: rule.windowToDay,
    toMonth: MonthLabels[rule.windowToMonth],
  });
}

/**
 * Returns a human-readable description of a schedule rule.
 */
export function formatScheduleRule(rule: FormattableRule): string {
  if (rule.type === undefined) return i18n.t('common:scheduleRule.formats.unknown');

  switch (rule.type) {
    case ScheduleRuleType.Weekly: {
      const days = (rule.weekdays ?? [])
        .filter((d) => WeekdayLabels[d])
        .map((d) => WeekdayLabels[d]);
      return days.length === 0
        ? i18n.t('common:scheduleRule.formats.everyWeek')
        : i18n.t('common:scheduleRule.formats.weeklyDays', { days: days.join(', ') });
    }
    case ScheduleRuleType.BiWeekly: {
      const window = formatWindow(rule);
      return window
        ? i18n.t('common:scheduleRule.formats.biWeeklyWindow', { window })
        : i18n.t('common:scheduleRule.formats.biWeekly');
    }
    case ScheduleRuleType.Monthly: {
      const window = formatWindow(rule);
      const ordinal = rule.ordinal != null ? OrdinalLabels[rule.ordinal] : '';
      const weekday = rule.weekday != null ? WeekdayLabels[rule.weekday] : '';
      if (!ordinal || !weekday) {
        // Day-free monthly: once per month, any day.
        return window
          ? i18n.t('common:scheduleRule.formats.everyMonthWindow', { window })
          : i18n.t('common:scheduleRule.formats.everyMonth');
      }
      if (window) {
        return i18n.t('common:scheduleRule.formats.monthly', { position: ordinal, weekday, window });
      }
      return i18n.t('common:scheduleRule.formats.monthlyShort', { position: ordinal, weekday });
    }
    case ScheduleRuleType.Seasonal: {
      const window = formatWindow(rule);
      return window ?? i18n.t('common:scheduleRule.formats.seasonal');
    }
    default:
      return i18n.t('common:scheduleRule.formats.unknown');
  }
}
